import {
  AlertBanner,
  Button,
  Loader,
  ModalIsland,
} from "@saviynt/design-system";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { getIntegrationTypeMeta } from "../../config/integrationTypeConfig";
import { useIntegrationById } from "../../hooks/useIntegrationById";
import { useIntegrationTypes } from "../../hooks/useIntegrationTypes";
import {
  useCreateAgentforceIntegration,
  useUpdateAgentforceIntegration,
} from "../agentforce";
import { useCreateIntegration } from "../aws/AWSIntegrationModal/useCreateIntegration";
import { useUpdateIntegration } from "../aws/AWSIntegrationModal/useUpdateIntegration";
import {
  useCreateAzureFoundryIntegration,
  useUpdateAzureFoundryIntegration,
} from "../azure-foundry";
import {
  useCreateCopilotStudioIntegration,
  useUpdateCopilotStudioIntegration,
} from "../CopilotStudio";
import {
  useCreateGcpIntegration,
  useUpdateGcpIntegration,
} from "../gcp-vertex";
import {
  useCreateServiceNowIntegration,
  useUpdateServiceNowIntegration,
} from "../servicenow";
import { IntegrationFormView } from "../IntegrationFormView";
import ModalFullPage from "../ModalFullPage";
import { IntegrationTypeSelector } from "./IntegrationTypeSelector";

const getFormKind = (type: string | null | undefined) =>
  getIntegrationTypeMeta(type)?.form;

interface IntegrationModalProps {
  isOpen: boolean;
  onClose: (alert?: {
    type: "success" | "error" | "draft";
    message: string;
  }) => void;
  integrationId?: string;
  /** When set (e.g. from /new?type=AWS or /edit?type=GOOGLE_VERTEX_DISCOVERY&id=1), form is shown directly. */
  initialSelectedType?: string;
}

export default function IntegrationModal({
  isOpen,
  onClose,
  integrationId,
  initialSelectedType,
}: IntegrationModalProps) {
  const isEditMode = !!integrationId;

  // Fetch integration data in edit mode
  const { data: integrationData, isLoading: isLoadingIntegration } =
    useIntegrationById(isEditMode ? Number(integrationId) : undefined);

  // Fetch integration types for the type-selector grid in add mode.
  const {
    data: integrationTypes,
    isLoading,
    error,
  } = useIntegrationTypes({ enabled: !isEditMode });

  // Use initialSelectedType from URL when provided (both /new and /edit?type=...); in edit mode without URL type we wait for integration data
  const [selectedType, setSelectedType] = useState<string | null>(
    initialSelectedType ?? (!integrationId ? null : null),
  );
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [cftDownloaded, setCftDownloaded] = useState(false);
  const [isDraftWarningOpen, setIsDraftWarningOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const intl = useIntl();

  // Update selectedType when integration data loads in edit mode (overrides URL type when API returns); add mode uses URL type
  useEffect(() => {
    if (isEditMode && integrationData?.integrationType) {
      setSelectedType(integrationData.integrationType);
    } else if (!isEditMode) {
      setSelectedType(initialSelectedType ?? null);
    }
  }, [isEditMode, integrationData?.integrationType, initialSelectedType]);

  // Reset form state when modal closes or integrationId changes
  useEffect(() => {
    if (!isOpen) {
      setSelectedType(null);
      setFormData({});
      setIsFormValid(false);
      setCftDownloaded(false);
      setIsDraftWarningOpen(false);
      setFormError(null);
    }
  }, [isOpen, integrationId]);

  const handleFormClose = (alert?: {
    type: "success" | "error" | "draft";
    message: string;
  }) => {
    if (alert) {
      onClose(alert);
    } else {
      onClose();
    }
    setSelectedType(null);
    setFormData({});
  };

  const createIntegrationMutation = useCreateIntegration({
    onSuccess: (message, isDraft) => {
      setFormError(null);
      handleFormClose({
        type: isDraft ? "draft" : "success",
        message,
      });
    },
    onError: (message) => {
      handleFormClose({ type: "error", message });
    },
  });

  const updateIntegrationMutation = useUpdateIntegration({
    integrationId: isEditMode ? Number(integrationId) : 0,
    onSuccess: (message, isDraft) => {
      handleFormClose({
        type: isDraft ? "draft" : "success",
        message,
      });
    },
    onError: (message) => {
      handleFormClose({ type: "error", message });
    },
  });

  const createGcpIntegrationMutation = useCreateGcpIntegration({
    onSuccess: (message, isDraft) => {
      setFormError(null);
      handleFormClose({
        type: isDraft ? "draft" : "success",
        message,
      });
    },
    onError: (message) => {
      setFormError(message);
    },
  });

  const updateGcpIntegrationMutation = useUpdateGcpIntegration({
    integrationId: isEditMode ? Number(integrationId) : 0,
    onSuccess: (message, isDraft) => {
      handleFormClose({
        type: isDraft ? "draft" : "success",
        message,
      });
    },
    onError: (message) => {
      setFormError(message);
    },
  });

  const createAgentforceIntegrationMutation = useCreateAgentforceIntegration({
    onSuccess: (message, isDraft) => {
      setFormError(null);
      handleFormClose({
        type: isDraft ? "draft" : "success",
        message,
      });
    },
    onError: (message) => {
      setFormError(message);
    },
  });

  const updateAgentforceIntegrationMutation = useUpdateAgentforceIntegration({
    integrationId: isEditMode ? Number(integrationId) : 0,
    onSuccess: (message, isDraft) => {
      handleFormClose({
        type: isDraft ? "draft" : "success",
        message,
      });
    },
    onError: (message) => {
      setFormError(message);
    },
  });

  const createCopilotStudioIntegrationMutation =
    useCreateCopilotStudioIntegration({
      onSuccess: (message, isDraft) => {
        setFormError(null);
        handleFormClose({
          type: isDraft ? "draft" : "success",
          message,
        });
      },
      onError: (message) => {
        setFormError(message);
      },
    });

  const updateCopilotStudioIntegrationMutation =
    useUpdateCopilotStudioIntegration({
      integrationId: isEditMode ? Number(integrationId) : 0,
      onSuccess: (message, isDraft) => {
        handleFormClose({
          type: isDraft ? "draft" : "success",
          message,
        });
      },
      onError: (message) => {
        setFormError(message);
      },
    });

  const createAzureFoundryIntegrationMutation =
    useCreateAzureFoundryIntegration({
      onSuccess: (message, isDraft) => {
        setFormError(null);
        handleFormClose({
          type: isDraft ? "draft" : "success",
          message,
        });
      },
      onError: (message) => {
        setFormError(message);
      },
    });

  const updateAzureFoundryIntegrationMutation =
    useUpdateAzureFoundryIntegration({
      integrationId: isEditMode ? Number(integrationId) : 0,
      onSuccess: (message, isDraft) => {
        handleFormClose({
          type: isDraft ? "draft" : "success",
          message,
        });
      },
      onError: (message) => {
        setFormError(message);
      },
    });

  const createServiceNowIntegrationMutation = useCreateServiceNowIntegration({
    onSuccess: (message, isDraft) => {
      setFormError(null);
      handleFormClose({
        type: isDraft ? "draft" : "success",
        message,
      });
    },
    onError: (message) => {
      setFormError(message);
    },
  });

  const updateServiceNowIntegrationMutation = useUpdateServiceNowIntegration({
    integrationId: isEditMode ? Number(integrationId) : 0,
    onSuccess: (message, isDraft) => {
      handleFormClose({
        type: isDraft ? "draft" : "success",
        message,
      });
    },
    onError: (message) => {
      setFormError(message);
    },
  });

  const isMutating =
    createIntegrationMutation.isPending ||
    updateIntegrationMutation.isPending ||
    createGcpIntegrationMutation.isPending ||
    updateGcpIntegrationMutation.isPending ||
    createAgentforceIntegrationMutation.isPending ||
    updateAgentforceIntegrationMutation.isPending ||
    createCopilotStudioIntegrationMutation.isPending ||
    updateCopilotStudioIntegrationMutation.isPending ||
    createServiceNowIntegrationMutation.isPending ||
    updateServiceNowIntegrationMutation.isPending ||
    createAzureFoundryIntegrationMutation.isPending ||
    updateAzureFoundryIntegrationMutation.isPending;

  const handleIntegrationSelect = (typeName: string) => {
    setSelectedType(typeName);
  };

  const handleSubmit = (isEnabled: boolean) => {
    const formKind = getFormKind(selectedType);
    if (formKind && formKind !== "AWS") setFormError(null);

    if (formKind === "GCPVertex") {
      if (isEditMode) {
        updateGcpIntegrationMutation.mutate({ isEnabled, data: formData });
      } else {
        createGcpIntegrationMutation.mutate({ isEnabled, data: formData });
      }
    } else if (formKind === "Agentforce") {
      if (isEditMode) {
        updateAgentforceIntegrationMutation.mutate({
          isEnabled,
          data: formData,
        });
      } else {
        createAgentforceIntegrationMutation.mutate({
          isEnabled,
          data: formData,
        });
      }
    } else if (formKind === "CopilotStudio") {
      if (isEditMode) {
        updateCopilotStudioIntegrationMutation.mutate({
          isEnabled,
          data: formData,
        });
      } else {
        createCopilotStudioIntegrationMutation.mutate({
          isEnabled,
          data: formData,
        });
      }
    } else if (formKind === "ServiceNow") {
      if (isEditMode) {
        updateServiceNowIntegrationMutation.mutate({
          isEnabled,
          data: formData,
        });
      } else {
        createServiceNowIntegrationMutation.mutate({
          isEnabled,
          data: formData,
        });
      }
    } else if (formKind === "AzureFoundry") {
      if (isEditMode) {
        updateAzureFoundryIntegrationMutation.mutate({
          isEnabled,
          data: formData,
        });
      } else {
        createAzureFoundryIntegrationMutation.mutate({
          isEnabled,
          data: formData,
        });
      }
    } else {
      if (isEditMode) {
        updateIntegrationMutation.mutate({
          integrationType: integrationData?.integrationType || "AWS",
          isEnabled,
          data: formData,
        });
      } else {
        createIntegrationMutation.mutate({
          integrationType: selectedType || "AWS",
          isEnabled,
          data: formData,
        });
      }
    }
  };

  const handleSaveAsDraft = () => {
    setIsDraftWarningOpen(true);
  };

  const handleDraftConfirm = () => {
    setIsDraftWarningOpen(false);
    handleSubmit(false);
  };

  const handleDraftDiscard = () => {
    setIsDraftWarningOpen(false);
  };

  // In edit mode, skip type selector and go directly to form
  // When type is in URL (e.g. /edit?type=GOOGLE_VERTEX_DISCOVERY&id=1), show GCP form immediately; API prefill can be added later
  if (isEditMode) {
    const editModeType =
      integrationData?.integrationType || selectedType || initialSelectedType;
    const initialFormKind = getFormKind(initialSelectedType);
    const hasCustomFormFromUrl = !!initialFormKind && initialFormKind !== "AWS";

    if (isLoadingIntegration && !hasCustomFormFromUrl) {
      return (
        <ModalFullPage
          isOpen={isOpen}
          onClose={onClose}
          closeOnEsc
          title={intl.formatMessage({
            id: "Jq276c",
            defaultMessage: "Edit Integration",
            description: "Page title for edit integration form",
          })}
        >
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader kind="loop" format="main" color="brand" size="large" />
          </div>
        </ModalFullPage>
      );
    }

    if (editModeType) {
      const editMeta = getIntegrationTypeMeta(editModeType);
      const editModeSubtitle = editMeta?.subtitle ?? editModeType;
      const editModeIcon = editMeta?.formIcon ?? editMeta?.icon ?? editModeType;
      return (
        <ModalFullPage
          isOpen={isOpen}
          onClose={handleFormClose}
          closeOnEsc
          title={intl.formatMessage({
            id: "Jq276c",
            defaultMessage: "Edit Integration",
            description: "Page title for edit integration form",
          })}
          subtitle={editModeSubtitle}
          icon={editModeIcon}
          primaryAction={{
            label: intl.formatMessage({
              id: "Xpo7B8",
              defaultMessage: "Update",
              description: "Update button label",
            }),
            onClick: () => handleSubmit(true),
            disabled: !isFormValid,
            isLoading: isMutating,
          }}
        >
          <IntegrationFormView
            selectedType={editModeType}
            integrationId={integrationId}
            formData={formData}
            isFormValid={isFormValid}
            onFormDataChange={(data) => {
              setFormData(data);
              setIsFormValid(Boolean(data.isValid));
              setCftDownloaded(Boolean(data.cftDownloaded));
            }}
            onClose={handleFormClose}
          />
        </ModalFullPage>
      );
    }
  }

  // Add mode: show form if type is selected
  if (selectedType) {
    return (
      <>
        <ModalFullPage
          isOpen={isOpen}
          onClose={handleFormClose}
          closeOnEsc
          title={intl.formatMessage({
            id: "YYujKG",
            defaultMessage: "Add Integration",
            description: "Page title for add integration form",
          })}
          subtitle={
            getIntegrationTypeMeta(selectedType)?.subtitle ?? selectedType
          }
          icon={
            getIntegrationTypeMeta(selectedType)?.formIcon ??
            getIntegrationTypeMeta(selectedType)?.icon ??
            selectedType
          }
          primaryAction={{
            label: intl.formatMessage({
              id: "ICzs5I",
              defaultMessage: "Integrate",
              description: "Integrate button label",
            }),
            onClick: () => handleSubmit(true),
            disabled: !isFormValid,
            isLoading: isMutating,
          }}
          secondaryAction={
            // Save as draft is only enabled for AWS Integration
            getFormKind(selectedType) === "AWS"
              ? {
                  label: intl.formatMessage({
                    id: "nqp4rb",
                    defaultMessage: "Save As Draft",
                    description: "Save as draft button label",
                  }),
                  onClick: handleSaveAsDraft,
                  disabled: !cftDownloaded,
                }
              : undefined
          }
        >
          {formError && (
            <div className="px-4 pt-4">
              <AlertBanner
                colorTheme="Critical"
                description={formError}
                kind="low"
                title={intl.formatMessage({
                  id: "zMWw5h",
                  defaultMessage: "Error",
                  description: "Error alert title",
                })}
                onCancel={() => setFormError(null)}
                shouldAutoDismiss={false}
              />
            </div>
          )}
          <IntegrationFormView
            selectedType={selectedType}
            formData={formData}
            isFormValid={isFormValid}
            onFormDataChange={(data) => {
              setFormData(data);
              setIsFormValid(Boolean(data.isValid));
              setCftDownloaded(Boolean(data.cftDownloaded));
              if (formError) setFormError(null);
            }}
            onClose={handleFormClose}
          />
        </ModalFullPage>
        {isDraftWarningOpen && (
          <div
            className="fixed inset-0 bg-gray-400/50 z-[9999]"
            onClick={handleDraftDiscard}
            aria-hidden="true"
          />
        )}
        <ModalIsland
          kind="warning"
          alertTitle={intl.formatMessage({
            id: "4yXAua",
            defaultMessage: "Unsaved Changes",
            description: "Unsaved changes modal title",
          })}
          alertSubtitle={intl.formatMessage({
            id: "8xMuWZ",
            defaultMessage:
              "You have unsaved changes to this integration. If you leave now, your progress will be lost.",
            description: "Unsaved changes modal subtitle",
          })}
          onClose={handleDraftDiscard}
          isOpen={isDraftWarningOpen}
          className="!z-[10000]"
          primaryButton={
            <Button
              type="button"
              kind="outlined"
              size="medium"
              onClick={handleDraftConfirm}
            >
              {intl.formatMessage({
                id: "oWWHnm",
                defaultMessage: "Save Draft",
                description: "Save draft button label",
              })}
            </Button>
          }
          secondaryButton={
            <Button
              type="button"
              kind="ghost"
              size="medium"
              onClick={handleDraftDiscard}
            >
              {intl.formatMessage({
                id: "VrcrPQ",
                defaultMessage: "Discard",
                description: "Discard button label",
              })}
            </Button>
          }
        />
      </>
    );
  }

  return (
    <ModalFullPage
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc
      title={intl.formatMessage({
        id: "YYujKG",
        defaultMessage: "Add Integration",
        description: "Page title for add integration form",
      })}
    >
      <IntegrationTypeSelector
        integrationTypes={integrationTypes || []}
        isLoading={isLoading}
        error={error}
        onSelect={handleIntegrationSelect}
      />
    </ModalFullPage>
  );
}
